import type { Claim, ClaimKind, ClaimStatus, Policy } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Input, LoadingState, Select, Sheet, Tabs, Textarea, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, shortDate } from "../theme";

/** Claims: first notice of loss through closure. The same cards serve the /claims screen and the customer file. */

type Row = { claim: Claim; customerName: string };
type View = "open" | "closed" | "all";
const KINDS: ClaimKind[] = ["auto_collision", "auto_comprehensive", "auto_liability", "auto_glass", "property", "liability", "injury", "other"];
const STATUSES: ClaimStatus[] = ["reported", "open", "under_review", "paid", "closed", "denied"];
const CLOSED = new Set<ClaimStatus>(["paid", "closed", "denied"]);
const dateOnly = (d: string) => (d.length === 10 ? `${d}T12:00:00` : d);

export function ClaimsScreen() {
  const t = useT();
  const [view, setView] = useState<View>("open");
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ["claims", view], queryFn: async () => (await callAction<"agency.claim.list", Row[]>("agency.claim.list", { status: view, limit: 500 })).result });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("claims.title")}</h1>
          <p className="text-ink/60">{t("claims.subtitle")}</p>
        </div>
        <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4" />{t("claims.add")}</Button>
      </div>
      <Tabs<View> value={view} onChange={setView} tabs={[{ id: "open", label: t("claims.filter.open") }, { id: "closed", label: t("claims.filter.closed") }, { id: "all", label: t("claims.filter.all") }]} />
      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data && q.data.length === 0 ? <EmptyState title={t("claims.none")} /> : null}
      {q.data?.length ? <div className="space-y-2">{q.data.map((r) => <ClaimCard key={r.claim.id} row={r} />)}</div> : null}
      <AddClaimSheet open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

export function ClaimCard({ row, compact }: { row: Row; compact?: boolean }) {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const { toast } = useToast();
  const qc = useQueryClient();
  const c = row.claim;
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ClaimStatus | "">("");
  const [note, setNote] = useState("");
  const [claimNumber, setClaimNumber] = useState(c.claimNumber ?? "");
  const [adjuster, setAdjuster] = useState(c.adjusterName ?? "");
  const [paid, setPaid] = useState(c.amountPaidCents != null ? String(c.amountPaidCents / 100) : "");
  const update = useMutation({
    mutationFn: () => callAction("agency.claim.update", { claimId: c.id, note: note || undefined, patch: { status: status || undefined, claimNumber: claimNumber !== (c.claimNumber ?? "") ? claimNumber : undefined, adjusterName: adjuster !== (c.adjusterName ?? "") ? adjuster : undefined, amountPaidCents: paid !== "" && Math.round(Number(paid) * 100) !== c.amountPaidCents ? Math.round(Number(paid) * 100) : undefined } }),
    onSuccess: () => { toast({ title: t("claims.updated"), tone: "success" }); setOpen(false); setNote(""); setStatus(""); for (const k of ["claims", "customer", "policy-history"]) qc.invalidateQueries({ queryKey: [k] }); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const tone = CLOSED.has(c.status) ? (c.status === "denied" ? "red" : "neutral") : c.status === "under_review" ? "amber" : "blue";
  const who = (id: string) => config.users.find((u) => u.id === id)?.name.split(" ")[0] ?? id;
  return (
    <Card className="px-4 py-3">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2">
            <Link to={`/customers/${c.customerId}`} className="font-bold hover:underline">{row.customerName}</Link>
            <Badge tone={tone}>{t(`claims.status.${c.status}`)}</Badge>
            <span className="text-sm text-ink/60">{t(`claims.kind.${c.kind}`)} · {c.carrier}{c.claimNumber ? ` · #${c.claimNumber}` : ""}</span>
          </p>
          <p className="mt-0.5 text-xs text-ink/55">
            {t("claims.dateOfLoss")} {shortDate(dateOnly(c.dateOfLoss), locale)} · {t("notices.received")} {shortDate(c.reportedAt, locale)}
            {c.adjusterName ? <> · {t("claims.adjuster")}: {c.adjusterName}{c.adjusterPhone ? ` ${c.adjusterPhone}` : ""}</> : null}
            {c.amountReservedCents != null ? <> · {t("claims.reserve")} {money(c.amountReservedCents)}</> : null}
            {c.amountPaidCents != null ? <> · {t("claims.paid")} {money(c.amountPaidCents)}</> : null}
            {c.deductibleCents != null ? <> · {t("claims.deductible")} {money(c.deductibleCents)}</> : null}
          </p>
          {c.description && !compact ? <p className="mt-1 text-sm text-ink/80">{c.description}</p> : null}
        </div>
        {!CLOSED.has(c.status) || open ? <Button size="sm" variant={open ? "primary" : "outline"} onClick={() => setOpen((v) => !v)}><LifeBuoy className="h-4 w-4" />{t("claims.addUpdate")}</Button> : null}
      </div>
      {!compact && c.updates.length ? (
        <ul className="mt-2 space-y-0.5 border-l-2 border-ink/10 pl-3 text-xs text-ink/70">
          {[...c.updates].reverse().map((u, i) => <li key={i}>{shortDate(u.at, locale)} · {u.note}{u.status ? ` · ${t(`claims.status.${u.status}`)}` : ""} <span className="text-ink/45">({who(u.userId)})</span></li>)}
        </ul>
      ) : null}
      {open ? (
        <form className="mt-2 grid gap-2 rounded-xl bg-ink/[0.03] p-3 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); update.mutate(); }}>
          <Select value={status} onChange={(e) => setStatus(e.target.value as ClaimStatus | "")} aria-label={t("claims.newStatus")}>
            <option value="">{t("claims.keepStatus")}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{t(`claims.status.${s}`)}</option>)}
          </Select>
          <Input value={claimNumber} onChange={(e) => setClaimNumber(e.target.value)} placeholder={t("claims.claimNumber")} aria-label={t("claims.claimNumber")} />
          <Input value={adjuster} onChange={(e) => setAdjuster(e.target.value)} placeholder={t("claims.adjuster")} aria-label={t("claims.adjuster")} />
          <Input type="number" step="0.01" min="0" value={paid} onChange={(e) => setPaid(e.target.value)} placeholder={t("claims.paid")} aria-label={t("claims.paid")} />
          <Textarea className="sm:col-span-2" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("claims.updateNote")} />
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" size="sm" loading={update.isPending}>{t("common.save")}</Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

export function AddClaimSheet({ open, onClose, customerId, policies }: { open: boolean; onClose: () => void; customerId?: string; policies?: Policy[] }) {
  const t = useT();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({ customerQuery: "", customerId: customerId ?? "", policyId: "", carrier: "", kind: "auto_collision" as ClaimKind, dateOfLoss: new Date().toISOString().slice(0, 10), description: "", claimNumber: "", adjusterName: "", adjusterPhone: "" });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const search = useQuery({ queryKey: ["customers", "claim-search", form.customerQuery], enabled: !customerId && form.customerQuery.trim().length >= 2, queryFn: async () => (await callAction<"agency.customer.search", Array<{ id: string; name: string; phone?: string }>>("agency.customer.search", { q: form.customerQuery, limit: 8 })).result });
  const create = useMutation({
    mutationFn: () => callAction("agency.claim.create", { customerId: form.customerId, policyId: form.policyId || undefined, carrier: form.carrier || undefined, kind: form.kind, status: "reported", dateOfLoss: form.dateOfLoss, description: form.description, claimNumber: form.claimNumber || undefined, adjusterName: form.adjusterName || undefined, adjusterPhone: form.adjusterPhone || undefined }),
    onSuccess: () => { toast({ title: t("claims.reported"), tone: "success" }); for (const k of ["claims", "customer", "today", "policy-history"]) qc.invalidateQueries({ queryKey: [k] }); onClose(); setForm((f) => ({ ...f, description: "", claimNumber: "", policyId: "", carrier: "" })); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const pols = policies ?? [];
  return (
    <Sheet open={open} onClose={onClose} title={t("claims.add")}>
      <form className="space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (form.customerId && (form.policyId || form.carrier.trim())) create.mutate(); }}>
        {!customerId ? (
          <div>
            <Input label={t("common.name")} value={form.customerQuery} onChange={set("customerQuery")} placeholder={t("faraday.chip.find.text")} />
            {search.data?.length ? (
              <ul className="mt-1 rounded-xl border border-ink/10 bg-white text-sm">
                {search.data.map((c) => <li key={c.id}><button type="button" onClick={() => setForm((f) => ({ ...f, customerId: c.id, customerQuery: c.name }))} className={cn("w-full px-3 py-2 text-left hover:bg-ink/5", form.customerId === c.id && "bg-primary/10 font-semibold")}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</button></li>)}
              </ul>
            ) : null}
          </div>
        ) : null}
        {pols.length ? (
          <Select label={t("claims.policy")} value={form.policyId} onChange={set("policyId")}>
            <option value="">{t("claims.noPolicy")}</option>
            {pols.map((p) => <option key={p.id} value={p.id}>{p.carrier} {p.line.replace(/_/g, " ")} {p.policyNumber}</option>)}
          </Select>
        ) : null}
        {!form.policyId ? <Input label={t("claims.carrier")} value={form.carrier} onChange={set("carrier")} required /> : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Select label={t("claims.kind")} value={form.kind} onChange={set("kind")}>{KINDS.map((k) => <option key={k} value={k}>{t(`claims.kind.${k}`)}</option>)}</Select>
          <Input label={t("claims.dateOfLoss")} type="date" value={form.dateOfLoss} onChange={set("dateOfLoss")} required />
          <Input label={t("claims.claimNumber")} value={form.claimNumber} onChange={set("claimNumber")} />
          <Input label={t("claims.adjuster")} value={form.adjusterName} onChange={set("adjusterName")} />
          <Input label={t("claims.adjusterPhone")} value={form.adjusterPhone} onChange={set("adjusterPhone")} />
        </div>
        <Textarea placeholder={t("claims.description")} value={form.description} onChange={set("description")} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" loading={create.isPending} disabled={!form.customerId}><LifeBuoy className="h-4 w-4" />{t("claims.add")}</Button>
        </div>
      </form>
    </Sheet>
  );
}

/** Claims on one customer, for the customer file. */
export function CustomerClaims({ customerId, policies }: { customerId: string; policies: Policy[] }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ["claims", "customer", customerId], queryFn: async () => (await callAction<"agency.claim.list", Row[]>("agency.claim.list", { status: "all", customerId, limit: 50 })).result });
  const rows = q.data ?? [];
  const open = rows.filter((r) => !CLOSED.has(r.claim.status));
  return (
    <Card>
      <CardHeader title={t("claims.customerCard")} action={<div className="flex items-center gap-2">{rows.length ? <Badge tone={open.length ? "amber" : "neutral"}>{open.length}</Badge> : null}<Button size="sm" variant="outline" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />{t("claims.add")}</Button></div>} />
      {rows.length ? <div className="px-4 pb-4 space-y-2">{[...open, ...rows.filter((r) => !open.includes(r)).slice(0, 3)].map((r) => <ClaimCard key={r.claim.id} row={r} />)}</div> : <p className="px-5 pb-4 text-sm text-ink/55">{t("common.empty")}</p>}
      <AddClaimSheet open={adding} onClose={() => setAdding(false)} customerId={customerId} policies={policies} />
    </Card>
  );
}
